import React, { useEffect } from 'react';
import { Button, Form, Input, Modal, message } from 'antd';
import type { EmployerCreateRequestDTO, EmployerDTO } from '@/shared/api/model';
import { useCreateEmployer, useUpdateEmployer } from '@/shared/api/client/employer-controller';

interface EmployerFormModalProps {
    open: boolean;
    employer?: EmployerDTO | null;
    onClose: () => void;
    onSaved: () => void;
}

export const EmployerFormModal: React.FC<EmployerFormModalProps> = ({ open, employer, onClose, onSaved }) => {
    const [form] = Form.useForm<EmployerCreateRequestDTO>();

    const createEmployer = useCreateEmployer({
        mutation: {
            onSuccess: (response) => {
                message.success((response as any)?.message || 'Employer created');
                onSaved();
            },
            onError: (error: any) => {
                const apiMessage = error?.response?.data?.message;
                message.error(apiMessage || 'Failed to create employer');
            }
        }
    });

    const updateEmployer = useUpdateEmployer({
        mutation: {
            onSuccess: (response) => {
                message.success((response as any)?.message || 'Employer updated');
                onSaved();
            },
            onError: (error: any) => {
                const apiMessage = error?.response?.data?.message;
                message.error(apiMessage || 'Failed to update employer');
            }
        }
    });

    useEffect(() => {
        if (!open) {
            form.resetFields();
            return;
        }

        form.setFieldsValue({
            name: employer?.name || '',
            voteNumber: employer?.voteNumber,
            address: employer?.address,
            poBox: employer?.poBox,
            phone: employer?.phone,
            email: employer?.email,
        });
    }, [employer, form, open]);

    const handleSubmit = async () => {
        const values = await form.validateFields();

        const payload: EmployerCreateRequestDTO = {
            ...values,
            name: (values.name || '').trim(),
            voteNumber: values.voteNumber ? values.voteNumber.trim() : undefined,
            address: values.address ? values.address.trim() : undefined,
            poBox: values.poBox ? values.poBox.trim() : undefined,
            phone: values.phone ? values.phone.trim() : undefined,
            email: values.email ? values.email.trim() : undefined,
        };

        if (employer?.id) {
            updateEmployer.mutate({ id: employer.id, data: payload });
        } else {
            createEmployer.mutate({ data: payload });
        }
    };

    const isSaving = createEmployer.isPending || updateEmployer.isPending;

    return (
        <Modal
            open={open}
            title={employer?.id ? 'Edit Employer' : 'Create Employer'}
            onCancel={onClose}
            footer={null}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: 'Please enter employer name' }]}
                >
                    <Input placeholder="Employer name" />
                </Form.Item>

                <Form.Item name="voteNumber" label="Vote Number"
                           rules={[{ required: true, message: 'Please enter Vote Number' }]}>
                    <Input placeholder="Vote number (must be unique)" />
                </Form.Item>

                <Form.Item name="address" label="Address">
                    <Input.TextArea placeholder="Address" />
                </Form.Item>

                <Form.Item name="poBox" label="P.O. Box">
                    <Input placeholder="P.O. Box" />
                </Form.Item>

                <Form.Item name="phone" label="Phone">
                    <Input placeholder="Phone" />
                </Form.Item>

                <Form.Item name="email" label="Email">
                    <Input placeholder="Email" />
                </Form.Item>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={handleSubmit} loading={isSaving}>
                        Save
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};
